/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// Ensure Algolia search input receives focus when search is opened
(function() {
  function focusAlgoliaInput() {
    const aaInput = document.querySelector('.aa-Autocomplete .aa-Input');
    if (aaInput) { aaInput.focus(); return true; }
    const nativeInput = document.querySelector('.main-search__input');
    if (nativeInput) { nativeInput.focus(); return true; }
    return false;
  }

  function tryFocusAlgoliaInput(maxAttempts = 15, intervalMs = 100) {
    let attempts = 0;
    const attempt = () => {
      if (focusAlgoliaInput()) return;
      if (++attempts < maxAttempts) setTimeout(attempt, intervalMs);
    };
    attempt();
  }

  // When user clicks the search trigger, retry focus as Algolia initializes after page load
  document.addEventListener('click', function(evt) {
    const trigger = evt.target && evt.target.closest && evt.target.closest('.show-search-link');
    if (!trigger) return;
    // Give header code a moment to open/search overlay
    setTimeout(tryFocusAlgoliaInput, 50);
  });

  // Also attempt on Algolia render events (results updated after init)
  document.addEventListener('algolia.instantSearch.onRender', function() {
    if (document.body.classList.contains('show-search')) {
      tryFocusAlgoliaInput();
    }
  });

  // Set placeholders on page load and when Algolia initializes
  function setSearchPlaceholders() {
    const inputs = [
      '.aa-Autocomplete .aa-Input',
      '.ais-SearchBox-input',
      '.main-search__input'
    ];

    inputs.forEach(selector => {
      const input = document.querySelector(selector);
      if (input && (!input.placeholder || input.placeholder.trim() === '')) {
        input.placeholder = 'Search';
      }
    });
  }

  // Set placeholders immediately
  setSearchPlaceholders();

  // Set placeholders when Algolia initializes
  document.addEventListener('algolia.hooks.initialize', setSearchPlaceholders);

  // Set placeholders when search overlay opens
  document.addEventListener('click', function(evt) {
    const trigger = evt.target && evt.target.closest && evt.target.closest('.show-search-link');
    if (trigger) {
      setTimeout(setSearchPlaceholders, 100);
    }
  });
})();


// Remove Zinrelo loaders when its iframe appears
(function() {
  const ZINRELO_HOST = 'app.zinrelo.com';

  function isZinreloIframe(element) {
    if (!element || element.tagName !== 'IFRAME') return false;
    const src = element.getAttribute('src') || element.src || '';
    return typeof src === 'string' && src.indexOf(ZINRELO_HOST) !== -1;
  }

  function clearZinreloLoaders() {
    const loaders = document.querySelectorAll('.zinrelo-points-item.item-loader');
    loaders.forEach((el) => el.classList.remove('item-loader'));
  }

  function scanForZinreloIframe() {
    const hasZinrelo = Array.from(document.querySelectorAll('iframe')).some(isZinreloIframe);
    if (hasZinrelo) clearZinreloLoaders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForZinreloIframe, { once: true });
  } else {
    scanForZinreloIframe();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // not an element
          if (isZinreloIframe(node)) {
            clearZinreloLoaders();
            return;
          }
          if (node.querySelectorAll) {
            const iframes = node.querySelectorAll('iframe');
            for (const iframe of iframes) {
              if (isZinreloIframe(iframe)) {
                clearZinreloLoaders();
                return;
              }
            }
          }
        });
      } else if (
        mutation.type === 'attributes' &&
        mutation.target &&
        mutation.target.tagName === 'IFRAME' &&
        mutation.attributeName === 'src'
      ) {
        if (isZinreloIframe(mutation.target)) {
          clearZinreloLoaders();
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });
})();

const iframe = document.getElementById("nvo_frame");

if (window.nvoObserver) {
  window.nvoObserver.disconnect();
}

let lastExternalHeight = 0;
let timeout;

if (iframe) {
  window.nvoObserver = new ResizeObserver(entries => {
    const observedHeight = Math.round(entries[0].contentRect.height);

    // If this equals what WE set before → ignore
    if (observedHeight === lastExternalHeight + getHeaderHeight()) {
      return;
    }

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      const headerPx = getHeaderHeight();

      // Save ONLY the external (Narvar) height
      lastExternalHeight = observedHeight;

      iframe.style.height = (observedHeight + headerPx) + "px";
    }, 250);
  });

  window.nvoObserver.observe(iframe);
}

// helper
function getHeaderHeight() {
  const val = getComputedStyle(document.documentElement)
    .getPropertyValue("--theme-sticky-header-height");

  return parseFloat(val) || 0;
}